import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUSES,
  ORDER_KIND_LABELS,
  PAYOUT_METHOD_LABELS,
  PAYOUT_METHODS,
  type CommissionCircuit,
  type CommissionDTO,
  type CommissionPageDTO,
  type CommissionStatus,
  type Paged,
  type PayoutDTO,
  type PayoutMethod,
  type SellerDTO,
} from '@bifeai/shared';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Kpi,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { BadgeDollarSign, CircleDollarSign, Clock, Undo2, Wallet } from 'lucide-react';
import { useState } from 'react';
import { CommissionStatusBadge, fieldsOf, Pager, parseAmount, RangeFilter, useRange } from '../components/sellers';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatMoney, formatUsd } from '../lib/format';
import { useCan } from '../lib/session';

const today = () => new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);

/** Comisiones de vendedores y partners (siempre en dólares) y los pagos que se les hicieron. */
export function CommissionsPage() {
  const [tab, setTab] = useState<'list' | 'payouts'>('list');
  return (
    <>
      <PageHeader
        title="Comisiones"
        subtitle="Lo que gana cada vendedor o partner por sus ventas, en dólares. Pagás varias juntas y queda el registro."
      />
      <div className="bf-view-switch" role="tablist" aria-label="Vista" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === 'list'} onClick={() => setTab('list')}>
          Comisiones
        </button>
        <button type="button" role="tab" aria-selected={tab === 'payouts'} onClick={() => setTab('payouts')}>
          Pagos hechos
        </button>
      </div>
      {tab === 'list' ? <CommissionsList /> : <PayoutsList />}
    </>
  );
}

function CommissionsList() {
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const r = useRange();
  const [payeeId, setPayeeId] = useState('');
  const [status, setStatus] = useState<CommissionStatus | ''>('PENDING');
  const [circuit, setCircuit] = useState<CommissionCircuit | ''>('');
  const [selected, setSelected] = useState<CommissionDTO[]>([]);
  const [paying, setPaying] = useState(false);
  const sellers = useQuery({ queryKey: ['sellers'], queryFn: () => api.get<SellerDTO[]>('/platform/sellers') });
  const extra = { payeeId, status, circuit };
  const list = useQuery({
    queryKey: ['commissions', r.qs(extra, 25)],
    queryFn: () => api.get<CommissionPageDTO>(`/platform/commissions?${r.qs(extra, 25)}`),
  });
  const d = list.data;
  const payee = selected[0]?.payeeId;
  const toggle = (c: CommissionDTO) => setSelected((s) => (s.some((x) => x.id === c.id) ? s.filter((x) => x.id !== c.id) : [...s, c]));
  const total = selected.reduce((a, c) => a + c.amountCents, 0);

  return (
    <>
      {d && (
        <div className="bf-kpi-grid" style={{ marginBottom: 18 }}>
          <Kpi label="A pagar" tone="amber" icon={<Clock />} value={formatUsd(d.totals.pendingCents)} delta="Con estos filtros" deltaMuted />
          <Kpi label="Pagado" tone="green" icon={<CircleDollarSign />} value={formatUsd(d.totals.paidCents)} />
          <Kpi label="Anulado" icon={<Undo2 />} value={formatUsd(d.totals.canceledCents)} delta="Ventas devueltas" deltaMuted />
        </div>
      )}
      <Card>
        <CardHeader
          title={
            <RangeFilter r={r}>
              <Select
                aria-label="Persona"
                value={payeeId}
                onChange={(e) => {
                  setPayeeId(e.target.value);
                  setSelected([]);
                  r.setPage(1);
                }}
                style={{ width: 'auto', minWidth: 180 }}
              >
                <option value="">A quién se le paga: todos</option>
                {(sellers.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Estado"
                style={{ width: 'auto' }}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as CommissionStatus | '');
                  setSelected([]);
                  r.setPage(1);
                }}
              >
                <option value="">Todos los estados</option>
                {COMMISSION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {COMMISSION_STATUS_LABELS[s]}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Circuito"
                style={{ width: 'auto' }}
                value={circuit}
                onChange={(e) => {
                  setCircuit(e.target.value as CommissionCircuit | '');
                  r.setPage(1);
                }}
              >
                <option value="">Propias y de equipo</option>
                <option value="OWN">Ventas propias</option>
                <option value="TEAM">Ventas de su equipo</option>
              </Select>
            </RangeFilter>
          }
          actions={
            can('commissions.pay') && (
              <Button variant="primary" icon={<Wallet />} disabled={!selected.length} onClick={() => setPaying(true)}>
                Pagar {selected.length ? `${selected.length} · ${formatUsd(total)}` : 'seleccionadas'}
              </Button>
            )
          }
        />
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 180 }} />
          ) : !d?.items.length ? (
            <EmptyState icon={<BadgeDollarSign />} title="Sin comisiones" text="Cuando un vendedor cierre una venta, su comisión aparece acá." />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    {can('commissions.pay') && <th aria-label="Elegir" />}
                    <th>Fecha</th>
                    <th>Gana</th>
                    <th>Venta</th>
                    <th style={{ textAlign: 'right' }}>Base</th>
                    <th style={{ textAlign: 'right' }}>Comisión</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((c) => {
                    const blocked = c.status !== 'PENDING' || (!!payee && payee !== c.payeeId);
                    return (
                      <tr key={c.id}>
                        {can('commissions.pay') && (
                          <td>
                            <input
                              type="checkbox"
                              aria-label="Elegir comisión"
                              checked={selected.some((x) => x.id === c.id)}
                              disabled={blocked}
                              title={payee && payee !== c.payeeId ? 'Un pago es para una sola persona' : undefined}
                              onChange={() => toggle(c)}
                            />
                          </td>
                        )}
                        <td className="bf-cell-sub">{formatDate(c.createdAt)}</td>
                        <td>
                          <span className="bf-cell-main">{c.sellerName}</span>
                          <div className="bf-cell-sub">
                            {c.circuit === 'TEAM' ? `Por su equipo (vendió ${c.sourceSellerName})` : 'Venta propia'}
                            {c.payeeId !== c.sellerId ? ` · se le paga a ${c.payeeName}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className="bf-cell-main">{c.tenantName ?? '—'}</span>
                          <div className="bf-cell-sub">
                            {ORDER_KIND_LABELS[c.orderKind]} · {c.orderDescription}
                          </div>
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          {formatUsd(c.baseCents)}
                          <div className="bf-cell-sub">{c.ratePct} %</div>
                        </td>
                        <td className="bf-num" style={{ textAlign: 'right' }}>
                          <strong>{formatUsd(c.amountCents)}</strong>
                        </td>
                        <td>
                          <CommissionStatusBadge status={c.status} />
                          {c.paidOn && <div className="bf-cell-sub">{formatDate(c.paidOn)}</div>}
                          {c.canceledReason && <div className="bf-cell-sub">{c.canceledReason}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {d && <Pager page={d.page} total={d.total} pageSize={d.pageSize} onPage={r.setPage} />}
      {paying && selected.length > 0 && (
        <PayoutDialog
          items={selected}
          seller={(sellers.data ?? []).find((s) => s.id === payee)}
          onClose={() => setPaying(false)}
          onDone={async () => {
            setPaying(false);
            setSelected([]);
            toast.success('Pago registrado');
            await qc.invalidateQueries({ queryKey: ['commissions'] });
            await qc.invalidateQueries({ queryKey: ['sellers'] });
          }}
        />
      )}
    </>
  );
}

function PayoutDialog({
  items,
  seller,
  onClose,
  onDone,
}: {
  items: CommissionDTO[];
  seller?: SellerDTO;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const total = items.reduce((a, c) => a + c.amountCents, 0);
  const [method, setMethod] = useState<PayoutMethod>('BANK');
  const [currency, setCurrency] = useState<'USD' | 'ARS'>('USD');
  const [fx, setFx] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rate = parseAmount(fx);
  const pay = [
    seller?.payoutAlias && `Alias ${seller.payoutAlias}`,
    seller?.payoutCbu && `CBU/CVU ${seller.payoutCbu}`,
    seller?.payoutWallet && `Cripto ${seller.payoutWallet}`,
    seller?.payoutNotes,
  ]
    .filter(Boolean)
    .join(' · ');

  const save = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await api.post('/platform/commissions/payouts', {
        commissionIds: items.map((c) => c.id),
        method,
        currency,
        fxRate: currency === 'ARS' ? rate : null,
        paidOn,
        reference,
        notes,
      });
      await onDone();
    } catch (err) {
      setErrors(fieldsOf(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Pagar a ${items[0]!.payeeName}`}>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Callout>
          {items.length} {items.length === 1 ? 'comisión' : 'comisiones'} por <strong>{formatUsd(total)}</strong>.
          {pay ? ` Datos de pago: ${pay}.` : ' No tiene datos de pago cargados en su ficha.'}
        </Callout>
        <div className="bf-form-row">
          <Field label="Cómo le pagaste">
            <Select value={method} onChange={(e) => setMethod(e.target.value as PayoutMethod)}>
              {PAYOUT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYOUT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Fecha" error={errors.paidOn}>
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Moneda">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'ARS')}>
              <option value="USD">Dólares</option>
              <option value="ARS">Pesos</option>
            </Select>
          </Field>
          {currency === 'ARS' && (
            <Field label="Dólar de ese día" hint="Pesos por dólar" error={errors.fxRate}>
              <Input inputMode="decimal" value={fx} onChange={(e) => setFx(e.target.value)} />
            </Field>
          )}
        </div>
        {currency === 'ARS' && rate > 0 && (
          <div className="bf-cell-sub">
            Le pagás <strong>{formatMoney(Math.round(total * rate))}</strong> ({formatUsd(total)} a {rate.toLocaleString('es-AR')}).
          </div>
        )}
        <Field label="Comprobante o referencia" hint="N° de operación, hash de la transacción…">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Notas">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} disabled={currency === 'ARS' && rate <= 0} onClick={() => void save()}>
            Registrar pago
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function PayoutsList() {
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const r = useRange();
  const [sellerId, setSellerId] = useState('');
  const [undoing, setUndoing] = useState<PayoutDTO | null>(null);
  const sellers = useQuery({ queryKey: ['sellers'], queryFn: () => api.get<SellerDTO[]>('/platform/sellers') });
  const list = useQuery({
    queryKey: ['payouts', r.qs({ sellerId })],
    queryFn: () => api.get<Paged<PayoutDTO>>(`/platform/commissions/payouts?${r.qs({ sellerId })}`),
  });
  const undo = useMutation({
    mutationFn: (p: PayoutDTO) => api.del(`/platform/commissions/payouts/${p.id}`),
    onSuccess: async () => {
      toast.success('Pago deshecho', 'Sus comisiones volvieron a pendientes.');
      setUndoing(null);
      await qc.invalidateQueries();
    },
    onError: (err) => toast.error('No se pudo deshacer', errorMessage(err)),
  });
  const d = list.data;
  return (
    <>
      <Card>
        <CardHeader
          title={
            <RangeFilter r={r}>
              <Select
                aria-label="Persona"
                value={sellerId}
                onChange={(e) => {
                  setSellerId(e.target.value);
                  r.setPage(1);
                }}
                style={{ width: 'auto', minWidth: 180 }}
              >
                <option value="">Todas las personas</option>
                {(sellers.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </RangeFilter>
          }
        />
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !d?.items.length ? (
            <EmptyState icon={<Wallet />} title="Sin pagos" />
          ) : (
            <PayoutsTable items={d.items} onUndo={can('commissions.pay') ? setUndoing : undefined} showName />
          )}
        </CardBody>
      </Card>
      {d && <Pager page={d.page} total={d.total} pageSize={d.pageSize} onPage={r.setPage} />}
      {undoing && (
        <Dialog open onOpenChange={(o) => !o && setUndoing(null)} title="¿Deshacer este pago?">
          <div className="bf-form">
            <Callout tone="warning">Para pagos cargados por error. Sus {undoing.commissions} comisiones vuelven a quedar pendientes.</Callout>
            <div className="bf-form-actions">
              <Button onClick={() => setUndoing(null)}>Cancelar</Button>
              <Button variant="danger" loading={undo.isPending} onClick={() => undo.mutate(undoing)}>
                Deshacer pago
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export function PayoutsTable({ items, onUndo, showName }: { items: PayoutDTO[]; onUndo?: (p: PayoutDTO) => void; showName?: boolean }) {
  return (
    <div className="bf-table-wrap">
      <table className="bf-table">
        <thead>
          <tr>
            <th>Fecha</th>
            {showName && <th>A quién</th>}
            <th>Cómo</th>
            <th style={{ textAlign: 'right' }}>Monto</th>
            <th>Referencia</th>
            {onUndo && <th aria-label="Acciones" />}
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id}>
              <td className="bf-cell-sub">{formatDate(p.paidOn)}</td>
              {showName && <td className="bf-cell-main">{p.sellerName}</td>}
              <td>
                {PAYOUT_METHOD_LABELS[p.method]}
                <div className="bf-cell-sub">
                  {p.commissions} {p.commissions === 1 ? 'comisión' : 'comisiones'}
                </div>
              </td>
              <td className="bf-num" style={{ textAlign: 'right' }}>
                <strong>{formatUsd(p.amountCents)}</strong>
                {p.currency === 'ARS' && p.localCents !== null && (
                  <div className="bf-cell-sub">
                    {formatMoney(p.localCents)} a {p.fxRate?.toLocaleString('es-AR')}
                  </div>
                )}
              </td>
              <td className="bf-cell-sub">{[p.reference, p.notes].filter(Boolean).join(' · ') || '—'}</td>
              {onUndo && (
                <td style={{ textAlign: 'right' }}>
                  <Button size="sm" variant="ghost" icon={<Undo2 />} onClick={() => onUndo(p)}>
                    Deshacer
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
